import { useTranslation } from 'react-i18next';
import { AssetDetailsLinkList } from 'src/components/assets/AssetDetailsLinkList';
import { SiteHeader } from 'src/components/ui/SiteHeader';
import { TAssetStage } from '@brickwise/asset-validation/dist/schema/enums';
import { Routes } from 'src/router/Routes';
import { isNullUndefinedOrEmpty } from 'src/util/utils';
import ApartmentOutlinedIcon from '@mui/icons-material/ApartmentOutlined';
import { useAsset } from '../../context-providers/AssetContext';
import { Formik } from 'formik';
import { useEffect, useState } from 'react';
import { NotaryDatesSection } from '../../components/assets/asset-notary/NotaryDatesSection';
import { NotaryInfoSection } from '../../components/assets/asset-notary/NotaryInfoSection';
import { useNotary } from '../../hooks/useNotary';
import { postNotary } from '../../api/stoApi';
import { PostNotaryPayload } from '../../models';
import { generalStore } from '../../stores/GeneralStore';
import { Alert, Snackbar } from '@mui/material';
import { useNavigate } from 'react-router-dom';

export const AssetNotary = () => {
  const { t } = useTranslation();
  const { asset } = useAsset();
  const [reload, setReload] = useState<boolean>(false);
  const { notary, loading } = useNotary(asset.id, reload);
  const [openSuccessAlert, setOpenSuccessAlert] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (asset && asset.type !== 'builderOwnerModel') {
      navigate(Routes.ASSETS.EDIT.replace(':assetId', asset.id));
    }
  }, [asset]);

  return (
    <>
      <SiteHeader
        breadcrumbs={[
          {
            link: Routes.ASSETS.ROOT,
            label: t('dashboard.breadcrumbs.assets'),
            icon: <ApartmentOutlinedIcon sx={{ mr: 0.5 }} fontSize="inherit" />,
          },
          {
            link: Routes.ASSETS.EDIT.replace(':assetId', asset?.id ?? ''),
            label: (isNullUndefinedOrEmpty(asset?.descriptions?.title) ? asset?.id : asset?.descriptions?.title) ?? '',
          },
          {
            link: Routes.ASSETS.RECORDS.replace(':assetId', asset?.id ?? ''),
            label: t('dashboard.breadcrumbs.records'),
          },
        ]}
        headline={asset ? (isNullUndefinedOrEmpty(asset.descriptions?.title) ? asset.id : (asset.descriptions.title as string)) : ''}
      >
        {asset && (
          <AssetDetailsLinkList
            assetId={asset.id}
            useSerialNumber={asset.useSerialNumber}
            active={'notary'}
            isPrimary={asset?.investmentDetails.stage === TAssetStage.primary}
            isSecondary={asset?.investmentDetails.stage === TAssetStage.secondary}
            isPreorder={asset?.investmentDetails.stage === TAssetStage.preorder}
            securityType={asset?.investmentDetails?.securityType}
          />
        )}
      </SiteHeader>
      {!loading && (
        <>
          <Formik
            initialValues={{ email: notary?.email, name: notary?.name }}
            onSubmit={async (values: PostNotaryPayload) => {
              if (values.email || values.name) {
                try {
                  await postNotary(asset.id, values);
                } catch (err) {
                  generalStore.setError(t('dashboard.assets.notary.info.errors.generic'));
                }
                setOpenSuccessAlert(true);
              }
            }}
          >
            {(props) => (
              <form onSubmit={props.handleSubmit}>
                <NotaryInfoSection />
              </form>
            )}
          </Formik>
          <NotaryDatesSection
            assetId={asset.id}
            dates={notary?.possibleDates || []}
            openSuccessAlert={() => {
              setReload(!reload);
              setOpenSuccessAlert(true);
            }}
          />
        </>
      )}
      <Snackbar
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        open={openSuccessAlert}
        autoHideDuration={6000}
        onClose={() => setOpenSuccessAlert(false)}
      >
        <Alert onClose={() => setOpenSuccessAlert(false)} severity="success" sx={{ width: '100%' }}>
          {t('dashboard.assets.addedit.successalert')}
        </Alert>
      </Snackbar>
    </>
  );
};
